import { NeedleImage } from "../../../../lib/alt1/capture";
import { Vector2 } from "../../../../lib/math";
import { async_lazy, lazy, LazyAsync } from "../../../../lib/Lazy";
import { ScreenRectangle } from "../../../../lib/alt1/ScreenRectangle";
import { util } from "../../../../lib/util/util";
import lodash from "lodash";
import { OCR } from "../../../../lib/alt1/OCR";
var index = util.index;
var stringSimilarity = util.stringSimilarity;
export class CapturedScan {
    constructor(body, first_line_knowledge = null) {
        this.body = body;
        this.first_line_knowledge = first_line_knowledge;
        this._raw_lines = lazy(() => {
            const COLORS = [[255, 255, 255], [239, 176, 99], [192, 192, 192], [255, 223, 0]];
            const data = this.body.getData();
            const lines = [];
            const start = (() => {
                if (!this.first_line_knowledge)
                    return 0;
                for (let y = CapturedScan.FONT.basey; y < this.body.size.y; y += CapturedScan.LINE_HEIGHT / 2) {
                    const line = OCR.readLine(data, CapturedScan.FONT, COLORS, this.first_line_knowledge.position.x, y, true);
                    if (!line.text)
                        continue;
                    if (stringSimilarity(line.text, this.first_line_knowledge.text) > 0.7) {
                        lines.push(line);
                        return y + CapturedScan.LINE_HEIGHT;
                    }
                }
            })();
            if (start == undefined) {
                return [];
            }
            for (let lineindex = 0; lineindex < CapturedScan.MAX_LINE_COUNT; lineindex++) {
                const y = start + lineindex * CapturedScan.LINE_HEIGHT;
                const line = OCR.findReadLine(data, CapturedScan.FONT, COLORS, 70, y, 40, 1);
                lines.push(line);
            }
            const from = lines.findIndex(l => l.text != "");
            const to = lodash.findLastIndex(lines, l => l.text != "");
            return lines.slice(from, to + 1);
        });
        this._lines = lazy(() => {
            const lines = this._raw_lines.get().map(l => l.text);
            const cleaned_lines = [];
            let text = "";
            for (let line of lines) {
                if (line == "" && text != "") {
                    cleaned_lines.push(text);
                    text = "";
                }
                else {
                    if (text != "")
                        text += " ";
                    text += line;
                }
            }
            if (text != "")
                cleaned_lines.push(text);
            return cleaned_lines;
        });
        this.paragraph_start_indices = lazy(() => {
            const indices = [0];
            this._raw_lines.get().forEach((line, i) => {
                if (line.text == "")
                    indices.push(i + 1);
            });
            return indices;
        });
        this._different_level = lazy(() => {
            if (this.checkRawLines([
                { index: -1, expected_text: "different level." },
                { index: -1, expected_text: "level." },
            ]))
                return true;
            if (this.checkRawLines([
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are to" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are too far away and" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "The orb glows as you scan." },
            ]))
                return false;
            return undefined;
        });
        this._meerkats_active = lazy(() => {
            if (this._different_level.get())
                return undefined;
            index(this.paragraph_start_indices.get(), -1);
            if (this.checkRawLines([
                { index: index(this.paragraph_start_indices.get(), -1), expected_text: "Your meerkats are" },
                { index: index(this.paragraph_start_indices.get(), -1), expected_text: "Your meerkats increase" },
                { index: -1, expected_text: "your scan range by 5." },
            ]))
                return true;
            if (this.checkRawLines([
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are to" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are too far away and" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "The orb glows as you scan." },
            ]))
                return false;
            return undefined;
        });
        this._triple = lazy(() => {
            if (this.checkRawLines([
                { index: this.paragraph_start_indices.get()[1], expected_text: "The orb glows as you scan." },
                { index: this.paragraph_start_indices.get()[1], expected_text: "The orb glows then flickers" },
            ]))
                return true;
            if (this.checkRawLines([
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are to" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "You are too far away and" },
                { index: this.paragraph_start_indices.get()[1], expected_text: "Try scanning a different" },
            ]))
                return false;
            return undefined;
        });
        this.center_of_text = lazy(() => {
            const lines = this._raw_lines.get();
            const first = lines[0].debugArea;
            const last = lines[lines.length - 1].debugArea;
            const last_line_color_fix = lines[lines.length - 1].text.startsWith("The coordinate is") ? CapturedScan.LINE_HEIGHT : 0;
            return Vector2.add(this.body.screen_rectangle.origin, {
                x: ~~(first.x + first.w / 2),
                y: (first.y + last.y + last_line_color_fix + CapturedScan.LINE_HEIGHT) / 2
            });
        });
        body.setName("scan");
    }
    checkRawLines(refs) {
        const THRESHOLD = 0.7;
        return refs.some(r => {
            const line = index(this._raw_lines.get(), r.index);
            if (line == undefined)
                return false;
            return stringSimilarity(line.text, r.expected_text) > THRESHOLD;
        });
    }
    text() {
        return this._lines.get().join("\n");
    }
    scanArea() {
        return this._lines.get()[0];
    }
    hasMeerkats() {
        return this._meerkats_active.get();
    }
    isDifferentLevel() {
        return this._different_level.get();
    }
    isTriple() {
        return this._triple.get();
    }
    screenRectangle() {
        const lines = this._raw_lines.get().length;
        return ScreenRectangle.move(this.body.screenRectangle(), { x: 0, y: 6 * lines - 74 }, { x: 180, y: 253 });
    }
    relevantTextAreaForRecapture() {
        const current_center = this.center_of_text.get();
        return {
            origin: Vector2.add(current_center, Vector2.scale(-0.5, CapturedScan.MAX_TEXT_AREA_SIZE)),
            size: CapturedScan.MAX_TEXT_AREA_SIZE
        };
    }
    updated(capture) {
        const relevant_text_area = this.relevantTextAreaForRecapture();
        const text_start_absolute = Vector2.add(this._raw_lines.get()[0].debugArea, this.body.screenRectangle().origin);
        const text_start_relative = Vector2.sub(text_start_absolute, relevant_text_area.origin);
        return new CapturedScan(capture.getScreenSection(relevant_text_area), {
            text: this._raw_lines.get()[0].text,
            position: Vector2.add(text_start_relative, { x: 0, y: CapturedScan.FONT.basey })
        });
    }
    isValid() {
        return this._raw_lines.get().length > 0;
    }
}
(function (CapturedScan) {
    CapturedScan.MAX_LINE_COUNT = 13;
    CapturedScan.LINE_HEIGHT = 16;
    CapturedScan.FONT = require("alt1/fonts/chatbox/12pt.js");
    CapturedScan.MAX_TEXT_AREA_SIZE = {
        x: 180,
        y: CapturedScan.MAX_LINE_COUNT * CapturedScan.LINE_HEIGHT
    };
    CapturedScan.finder = async_lazy(async () => {
        const anchor_images = await CapturedScan.anchors.get();
        return new class {
            find(screen) {
                const anchors = [{
                        img: anchor_images.scanfartext,
                        name: "Too Far",
                        origin_offset: { x: -20, y: 8 - CapturedScan.LINE_HEIGHT * 4 }
                    }, {
                        img: anchor_images.orbglows,
                        name: "Orb Glows",
                        origin_offset: { x: -21, y: 9 - CapturedScan.LINE_HEIGHT * 4 }
                    }, {
                        img: anchor_images.scanleveltext,
                        name: "Different Level",
                        origin_offset: { x: -20, y: 9 - CapturedScan.LINE_HEIGHT * 4 }
                    }];
                const found_body = (() => {
                    for (let anchor of anchors) {
                        let locs = screen.findNeedle(anchor.img);
                        if (locs.length > 0) {
                            return screen.getSubSection(ScreenRectangle.move(locs[0].screenRectangle(), anchor.origin_offset, { x: 180, y: 190 }));
                        }
                    }
                })();
                if (!found_body)
                    return;
                return new CapturedScan(found_body);
            }
        };
    });
    CapturedScan.anchors = new LazyAsync(async () => {
        return {
            scanleveltext: await NeedleImage.fromURL("/alt1anchors/scans/try_scanning_a_different_level.png"),
            scanfartext: await NeedleImage.fromURL("/alt1anchors/scans/you_are_too_far_away.png"),
            orbglows: await NeedleImage.fromURL("/alt1anchors/scans/the_orb_glows.png"),
        };
    });
})(CapturedScan || (CapturedScan = {}));
